import React from "react";

/** Tres vistas del MISMO par de zapatillas (las de "Un solo producto" de Mi marca). */
const VISTAS_DE_UN_PRODUCTO = [
  "/images/multi-vistas/zapatillas/frente.webp",
  "/images/multi-vistas/zapatillas/perspectiva.webp",
  "/images/multi-vistas/zapatillas/lateral.webp",
];

/**
 * Cuatro productos DISTINTOS de moda (de los de "Varios productos a la vez" de Mi marca).
 * Cuatro y no seis: la tarjeta mide ~130px y en 3×2 cada foto quedaba de 30px.
 */
const PRODUCTOS_DISTINTOS = [
  "/images/multi-vistas/otros/cartera-nobg.png",
  "/images/subir-productos/jean.png",
  "/images/subir-productos/remera.png",
  "/images/subir-productos/zapatillas.png",
];

const TITULOS = { uno: "Subir un producto", varios: "Subir varios productos" } as const;

const FOTO =
  "rounded-md bg-white object-cover shadow-[0_6px_14px_-6px_rgba(16,24,40,0.45)] ring-2 ring-white";

/**
 * Las vistas de un producto en abanico: la del medio al frente, las de los costados
 * inclinadas. En hover se abren un poco más (como la tarjeta de Mi marca).
 */
const AbanicoDeVistas: React.FC = () => (
  <span aria-hidden className="relative block h-full w-full">
    {VISTAS_DE_UN_PRODUCTO.map((src, i) => (
      <img
        key={src}
        src={src}
        alt=""
        loading="lazy"
        // Medidas por el ALTO de la caja, para que el abanico la llene de arriba abajo como
        // el mosaico de "Varios" y las dos tarjetas dejen el mismo aire hasta el título. La
        // del medio mide el alto entero; las de los costados, el 88%: inclinadas 10° su
        // esquina más baja cae justo en el borde de abajo, no por debajo.
        className={`${FOTO} absolute left-1/2 top-1/2 aspect-[4/5] w-auto transition-transform duration-500 ease-[cubic-bezier(0.3,0.8,0.3,1)] motion-reduce:transition-none ${
          i === 0
            ? "h-[88%] -translate-x-[112%] -translate-y-1/2 -rotate-[10deg] group-hover:-translate-x-[122%] group-hover:-rotate-[13deg]"
            : i === 2
              ? "h-[88%] translate-x-[12%] -translate-y-1/2 rotate-[10deg] group-hover:translate-x-[22%] group-hover:rotate-[13deg]"
              : "z-10 h-full -translate-x-1/2 -translate-y-1/2 group-hover:scale-[1.06]"
        }`}
      />
    ))}
  </span>
);

/** Productos distintos en un mosaico de 2×2. En hover se levantan en ola. */
const MosaicoDeProductos: React.FC = () => (
  <span aria-hidden className="grid h-full w-full grid-cols-2 content-center gap-1.5">
    {PRODUCTOS_DISTINTOS.map((src, i) => (
      <img
        key={src}
        src={src}
        alt=""
        loading="lazy"
        className={`${FOTO} aspect-square w-full transition-transform duration-300 ease-out group-hover:-translate-y-1 motion-reduce:transition-none`}
        style={{ transitionDelay: `${(i % 2) * 50 + Math.floor(i / 2) * 70}ms` }}
      />
    ))}
  </span>
);

/**
 * Una de las DOS tarjetas de subida que encabezan la grilla de productos de los modos —
 * ADR-2026-10-14-subir-productos-en-un-pop-up-desde-modelo. "Un producto" (sus vistas en
 * abanico) y "Subir varios productos" (un mosaico de productos distintos): las dos formas de la
 * pantalla "Subir productos" de Mi marca, con la cara de su tarjeta (punteado celeste,
 * degradé y halo). Tocarla abre el pop-up de esa forma.
 *
 * Van las dos en una fila PROPIA, arriba de la grilla y repartiéndose el ancho
 * (ADR-2026-10-14-tarjetas-de-subida-a-todo-el-ancho): metidas como celdas de la grilla
 * ocupaban dos de tres lugares de la primera fila y los productos empezaban más abajo.
 * Con tres o más columnas son apaisadas (`aspecto`), así la fila mide como una de
 * productos; con dos, cuadradas.
 */
export const TarjetaDeSubidaEnGrilla: React.FC<{
  variante: "uno" | "varios";
  onClick: () => void;
  /** La proporción de la tarjeta, como clase literal (`aspect-square`, `aspect-[3/2]`). */
  aspecto: string;
  redondeo: string;
}> = ({ variante, onClick, aspecto, redondeo }) => {
  const titulo = TITULOS[variante];
  const visual = variante === "uno" ? <AbanicoDeVistas /> : <MosaicoDeProductos />;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={titulo}
      className={`group relative overflow-hidden ${redondeo} border-2 border-dashed border-[rgb(var(--brand-primary-rgb)_/_0.215)] bg-gradient-to-b from-[rgb(var(--brand-primary-rgb)_/_0.07)] to-[rgb(var(--brand-primary-rgb)_/_0.03)] text-left transition-[border-color,box-shadow,transform] duration-300 hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgb(var(--brand-primary-rgb)_/_0.45)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary`}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(60%_70%_at_50%_0%,rgb(var(--brand-primary-rgb)_/_0.14),transparent)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
      />
      {/* Sólo el título: con una bajada, a ~130px de ancho se partía en dos renglones y
          empujaba las fotos. El visual se mide por el ALTO (no el ancho), así en una
          tarjeta apaisada no se agranda hasta salirse. */}
      <span
        className={`relative flex ${aspecto} flex-col items-center justify-center gap-2.5 px-3`}
      >
        <span
          className={`block h-[56%] ${variante === "uno" ? "aspect-[5/3]" : "aspect-square"}`}
        >
          {visual}
        </span>
        <span className="block text-center text-[12.5px] font-semibold leading-tight text-[#16181D] sm:text-[13px]">
          {titulo}
        </span>
      </span>
    </button>
  );
};
